import { rebuildChildren } from '../../global/ui-helpers';
import { UIIcon } from './ui-icon';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { ElementChild, ElementConfig } from '../ui-element.type';
import { UIIconSymbolicShape } from './ui-icon-symbolic.helpers';
import styles from './ui-icon-symbolic.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIIcon}
 * @alias UIIconSymbolic
 * @element ui-icon-symbolic
 * @classdesc Represents a class for <code>ui-icon-symbolic</code> element.
 * @property {string} value {@attr value} Symbol(s) for the inner content.
 * @property {UIIconSymbolicShape} [bgshape] {@attr bgshape} Shape for the background.
 * @property {UIIconGlyph} [badgeGlyph] {@attr badge-glyph} Glyph (symbol) icon
 * from library provided by branding for badge.
 * @property {HTMLSpanElement} badge Container for badge icon.
 * @example
 * <ui-icon-symbolic value="dd"></ui-icon-symbolic>
 */
@Component({
    tag: 'ui-icon-symbolic',
    styles,
})
export class UIIconSymbolic extends UIIcon {
    @Attribute(String) value?: string;
    @Attribute(String) bgshape?: UIIconSymbolicShape =
        UIIconSymbolicShape.Circle;

    static get observedAttributes() {
        return [...super.observedAttributes, 'value', 'badge-glyph'];
    }

    /**
     * Build and return config for element with symbols.
     * @private
     * @returns {ElementConfig}
     */
    private buildSymbols(): ElementConfig {
        return {
            tagName: 'span',
            classList: {
                'ui-icon-symbolic__content': true,
            },
            children: [
                document.createTextNode(
                    String(this.value).substring(0, 2).toUpperCase()
                ),
            ],
        };
    }

    /**
     * Render or rebuild ui-icon mark up.
     */
    renderIconLayout(): void {
        const newChildren: Array<ElementChild> = [];
        if (this.value) {
            newChildren.push(this.buildSymbols());
        }
        if (this.badgeGlyph) {
            newChildren.push(this.buildBadge());
        }
        rebuildChildren(this, newChildren);
        this.classList.toggle('-badged', !!this.badgeGlyph);
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: string | null,
        newValue: string | null
    ): void {
        switch (name) {
            case 'value':
            case 'badge-glyph':
                this.renderIconLayout();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.renderIconLayout();
    }
}
